import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCw, X, Hand } from 'lucide-react';
import { DEFAULT_360_FRAMES } from '@/data/product360Frames';

/**
 * Product360Viewer
 * ---------------
 * Interactive 8-frame rotation viewer for a product. Displays a single
 * image at a time and cycles through the supplied frames on:
 *
 *   • horizontal click-and-drag (mouse + touch, via Pointer Events)
 *   • the on-screen left / right chevron buttons
 *   • the left / right arrow keys (when the viewer is focused)
 *
 * All 8 images are preloaded on mount so scrubbing feels instant. A
 * 360° label with a "drag to spin" hint sits on the image so the
 * affordance is obvious.
 *
 * Props:
 *   frames        Array of image URLs. Defaults to DEFAULT_360_FRAMES.
 *   productName   For alt text and the small caption strap.
 *   className     Extra classes for the outer wrapper.
 *   dragSensitivity  Pixels of horizontal drag per frame change.
 *                    Lower = more responsive; default 24.
 */
export default function Product360Viewer({
  frames = DEFAULT_360_FRAMES,
  productName = 'Product',
  className = '',
  dragSensitivity = 24,
}) {
  const frameCount = frames.length;
  const [currentFrame, setCurrentFrame] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const dragStateRef = useRef({ startX: 0, startFrame: 0, pointerId: null });
  const containerRef = useRef(null);

  // Preload every frame so rotation feels instant after the first
  // paint. Runs once per frames array.
  useEffect(() => {
    const imgs = frames.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });
    return () => imgs.forEach((i) => (i.src = ''));
  }, [frames]);

  const wrap = (n) => ((n % frameCount) + frameCount) % frameCount;

  const changeFrame = (next) => {
    setCurrentFrame(wrap(next));
    if (!hasInteracted) setHasInteracted(true);
  };

  // --- Pointer (mouse / touch / pen) drag ---
  const onPointerDown = (e) => {
    if (!containerRef.current) return;
    containerRef.current.setPointerCapture?.(e.pointerId);
    dragStateRef.current = {
      startX: e.clientX,
      startFrame: currentFrame,
      pointerId: e.pointerId,
    };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStateRef.current.startX;
    const delta = Math.round(dx / dragSensitivity);
    if (delta === 0) return;
    const next = wrap(dragStateRef.current.startFrame + delta);
    if (next !== currentFrame) {
      setCurrentFrame(next);
      if (!hasInteracted) setHasInteracted(true);
    }
  };

  const stopDrag = (e) => {
    if (!dragging) return;
    if (containerRef.current && dragStateRef.current.pointerId != null) {
      try {
        containerRef.current.releasePointerCapture?.(dragStateRef.current.pointerId);
      } catch {
        /* pointer already released */
      }
    }
    setDragging(false);
  };

  // --- Keyboard support ---
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      changeFrame(currentFrame - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      changeFrame(currentFrame + 1);
    }
  };

  // A rotating tick strip beneath the image gives a strong visual
  // cue of "how far round" the customer has spun the bike.
  const ticks = useMemo(() => Array.from({ length: frameCount }), [frameCount]);

  const angleLabel = `${currentFrame * (360 / frameCount)}°`;

  return (
    <div className={`w-full ${className}`}>
      <div
        ref={containerRef}
        role="img"
        aria-label={`${productName} 360-degree viewer, currently at ${angleLabel}`}
        aria-roledescription="Interactive 360-degree product viewer. Use left and right arrow keys, on-screen buttons, or drag to rotate."
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onPointerLeave={stopDrag}
        onKeyDown={onKeyDown}
        data-testid="product-360-viewer"
        className={`relative aspect-square bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)] overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-gold)] ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        style={{ touchAction: 'pan-y' }}
      >
        {/* Actual frame. Native <img> so it's cached by the browser
            with the preloaded pool. `draggable=false` prevents the
            browser's default image-drag ghost. */}
        <img
          src={frames[currentFrame]}
          alt={`${productName} — angle ${angleLabel}`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          data-testid={`product-360-frame-${currentFrame}`}
        />

        {/* 360° affordance badge — top-left. Small, quiet, unmistakable. */}
        <div
          className="absolute top-3 left-3 flex items-center gap-1.5 bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] px-2.5 py-1 text-[10px] tracking-[0.24em] uppercase font-semibold"
          data-testid="product-360-badge"
        >
          <RotateCw size={11} strokeWidth={2.5} />
          360° View
        </div>

        {/* Drag hint — pulses gently until the customer has spun the
            bike at least once, then fades out to stop being noisy. */}
        {!hasInteracted && (
          <div
            className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none"
            aria-hidden="true"
          >
            <div className="inline-flex items-center gap-2 bg-[var(--brand-forest-deep)]/85 text-[var(--brand-parchment)] px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase animate-pulse">
              <Hand size={13} />
              Drag or use arrows to spin
            </div>
          </div>
        )}

        {/* Left / right rotate buttons — always visible. Sized big
            enough for a proper 44 px touch target on mobile.
            `stopPropagation` on pointer + click stops the container's
            drag handler from swallowing the button interaction. */}
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            changeFrame(currentFrame - 1);
          }}
          aria-label="Rotate left"
          data-testid="product-360-prev"
          className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-[var(--brand-parchment)]/90 hover:bg-[var(--brand-parchment)] border border-[var(--brand-stone)] hover:border-[var(--brand-gold)] text-[var(--brand-forest)] transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            changeFrame(currentFrame + 1);
          }}
          aria-label="Rotate right"
          data-testid="product-360-next"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-[var(--brand-parchment)]/90 hover:bg-[var(--brand-parchment)] border border-[var(--brand-stone)] hover:border-[var(--brand-gold)] text-[var(--brand-forest)] transition-colors"
        >
          <ChevronRight size={22} />
        </button>

        {/* Progress ring — 8 ticks along the bottom, current one gold. */}
        <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[var(--brand-forest-deep)]/70 px-2.5 py-1.5">
          {ticks.map((_, i) => (
            <button
              key={i}
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                changeFrame(i);
              }}
              aria-label={`Jump to angle ${i * (360 / frameCount)} degrees`}
              className={`h-1.5 rounded-full transition-all ${
                i === currentFrame
                  ? 'w-4 bg-[var(--brand-gold)]'
                  : 'w-1.5 bg-[var(--brand-parchment)]/50 hover:bg-[var(--brand-parchment)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Caption strap — sits below the viewer, gives a clear "0° to 360°" narrative. */}
      <div className="mt-2.5 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-[var(--brand-muted)]">
        <span>Angle {angleLabel}</span>
        <span className="italic normal-case tracking-normal opacity-70">
          Drag &middot; Arrows &middot; Tap ticks to jump
        </span>
      </div>
    </div>
  );
}

/**
 * Product360Modal
 * ---------------
 * Modal wrapper around Product360Viewer. Handles the "open in viewer"
 * flow launched from a product card: dims the page, traps focus, and
 * closes on Esc or backdrop click.
 */
export function Product360Modal({ open, onClose, frames, productName }) {
  // Prevent background scroll while open, close on Esc.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${productName} 360-degree viewer`}
      data-testid="product-360-modal"
      className="fixed inset-0 z-[110] grid place-items-center bg-black/70 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-[var(--brand-parchment)] border border-[var(--brand-stone)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--brand-stone)] px-5 py-4">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[var(--brand-muted)]">
              360° Rotation Viewer
            </div>
            <div className="font-display text-xl text-[var(--brand-forest)]">
              {productName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close 360-degree viewer"
            data-testid="product-360-close"
            className="w-10 h-10 grid place-items-center text-[var(--brand-forest)] hover:bg-[var(--brand-parchment-warm)] transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        {/* Viewer */}
        <div className="p-5 sm:p-8">
          <Product360Viewer frames={frames} productName={productName} />
          <p className="mt-5 text-xs italic text-[var(--brand-muted)] leading-relaxed">
            These renders are AI-generated placeholders while our team
            arranges real product photography with the factory. The
            spec is faithful; the paintwork is representative.
          </p>
        </div>
      </div>
    </div>
  );
}
